import { FormInstance, message, Modal } from 'antd';
import { NavigateFunction } from 'react-router-dom';
import { ApiService } from 'service/api/apiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { PeopleInterface } from 'type/peopleType';
import { CastFormData } from '../../../../shared/types/i18n/i18nTypes';
import {
  createCastApiRequest,
  validateCastRequiredFields,
} from '../../../../shared/services/i18n/i18nService';

class PeopleService {
  constructor(private apiService: ApiService<PeopleInterface>) {}

  public async createPeople(form: FormInstance, navigate: NavigateFunction): Promise<void> {
    try {
      const peopleFormData = this.extractPeopleFromForm(form);
      await this.apiService.createByFormData(peopleFormData);
      navigate('/people');
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: 'Failed to create : ' + error,
        okText: 'Confirm',
      });
    }
  }

  public async updatePeople(
    id: number,
    form: FormInstance,
    navigate: NavigateFunction
  ): Promise<void> {
    try {
      const peopleFormData = this.extractPeopleFromForm(form);
      await this.apiService.updateByFormData(id, peopleFormData);
      message.success('Updated successfully');
      navigate('/people');
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: 'Failed to update : ' + error,
        okText: 'Confirm',
      });
    }
  }

  /**
   * Cast 생성 (i18n 지원)
   */
  public async createCastWithI18n(
    castData: CastFormData,
    navigate: NavigateFunction
  ): Promise<void> {
    try {
      // 필수 필드 검증
      const validationErrors = validateCastRequiredFields(castData);
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.map((error: any) => error.message).join(', ');
        Modal.error({
          title: 'Error',
          content: `Validation error: ${errorMessage}`,
          okText: 'Confirm',
        });
        return;
      }

      // API 요청 데이터 생성
      const apiRequest = createCastApiRequest(castData);
      await peopleApiService.createCastWithI18n(apiRequest);

      message.success('Cast has been created successfully');
      navigate('/people');
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: 'Failed to create Cast: ' + error,
        okText: 'Confirm',
      });
    }
  }

  /**
   * Cast 업데이트 (i18n 지원)
   */
  public async updateCastWithI18n(
    id: number,
    castData: CastFormData,
    navigate: NavigateFunction
  ): Promise<void> {
    try {
      // 필수 필드 검증
      const validationErrors = validateCastRequiredFields(castData);
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.map((error: any) => error.message).join(', ');
        Modal.error({
          title: 'Error',
          content: `Validation error: ${errorMessage}`,
          okText: 'Confirm',
        });
        return;
      }

      // API 요청 데이터 생성
      const apiRequest = createCastApiRequest(castData);
      await peopleApiService.updateCastWithI18n(id, apiRequest);

      message.success('Cast has been updated successfully');
      navigate('/people');
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: 'Failed to update Cast: ' + error,
        okText: 'Confirm',
      });
    }
  }

  private extractPeopleFromForm(form: FormInstance): FormData {
    const formData = new FormData();
    formData.append('name', form.getFieldValue('name'));
    formData.append('slug', form.getFieldValue('slug'));
    if (form.getFieldValue('name_en')) {
      formData.append('name_en', form.getFieldValue('name_en'));
    }
    if (form.getFieldValue('birth_date')) {
      formData.append('birth_date', form.getFieldValue('birth_date'));
    }
    if (form.getFieldValue('nationality')) {
      formData.append('nationality', form.getFieldValue('nationality'));
    }
    if (form.getFieldValue('thumbnail')) {
      formData.append('thumbnail', form.getFieldValue('thumbnail'));
    }
    if (form.getFieldValue('type')) {
      formData.append('type', form.getFieldValue('type'));
    }
    return formData;
  }
}

export const peopleService = new PeopleService(peopleApiService);
